import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Card, IconButton } from '../ui';
import { Check, Edit2, X, Plus } from 'lucide-react';

const SummaryForm = ({ initialData, onSave, isLoading }) => {
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      user_id: initialData?.user_id || '',
      profile_id: initialData?.profile_id || '',
      business_name: initialData?.business_name || '',
      logo: initialData?.logo || '',
      industry: initialData?.industry || '',
      country: initialData?.country || '',
      city: initialData?.city || '',
      description: initialData?.description || '',
      audience: initialData?.audience || '',
      selling_points: initialData?.selling_points || [''],
      call_to_actions: initialData?.call_to_actions || [''],
      core_values: initialData?.core_values || [''],
      voice_tone: initialData?.voice_tone || '',
    },
  });

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      reset({
        user_id: initialData?.user_id || '',
        profile_id: initialData?.profile_id || '',
        business_name: initialData?.business_name || '',
        logo: initialData?.logo || '',
        industry: initialData?.industry || '',
        country: initialData?.country || '',
        city: initialData?.city || '',
        description: initialData?.description || '',
        audience: initialData?.audience || '',
        selling_points: initialData?.selling_points || [''],
        call_to_actions: initialData?.call_to_actions || [''],
        core_values: initialData?.core_values || [''],
        voice_tone: initialData?.voice_tone || '',
      });
    }
  }, [initialData, reset]);

  const formValues = watch();

  const {
    fields: sellingPointsFields,
    append: appendSellingPoint,
    remove: removeSellingPoint,
  } = useFieldArray({
    control,
    name: 'selling_points',
  });

  const {
    fields: ctaFields,
    append: appendCta,
    remove: removeCta,
  } = useFieldArray({
    control,
    name: 'call_to_actions',
  });

  const {
    fields: coreValuesFields,
    append: appendCoreValue,
    remove: removeCoreValue,
  } = useFieldArray({
    control,
    name: 'core_values',
  });

  const onSubmit = (data) => {
    // Extract only the fields needed for the API, excluding user_id and profile_id
    const { user_id, profile_id, industry, ...summaryData } = data;
    onSave(summaryData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  return (
    <Card className='bg-transparent border-0 shadow-none'>
      <Card.Header className='border-b-0 pl-0'>
        <div className='flex items-start justify-between'>
          <div>
            <h3 className='text-lg font-semibold text-gray-900 '>Business Summary</h3>
            <p className='text-sm text-gray-500 mt-1'>Review and edit your business information</p>
          </div>
          <div className='flex items-center gap-2'>
            {isEditing ? (
              <>
                <IconButton
                  icon={Check}
                  tooltip='Save changes'
                  onClick={handleSubmit(onSubmit)}
                  variant='primary'
                  disabled={!isDirty || isLoading}
                />
                <IconButton
                  icon={X}
                  tooltip='Cancel editing'
                  onClick={handleCancel}
                  variant='default'
                />
              </>
            ) : (
              <IconButton
                icon={Edit2}
                tooltip='Edit summary'
                onClick={() => setIsEditing(true)}
                variant='primary'
              />
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className='px-0'>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          {/* Hidden fields */}
          <input type='hidden' {...register('user_id')} />
          <input type='hidden' {...register('profile_id')} />

          {/* Header Section with Logo */}
          <div className='flex items-start justify-between gap-6'>
            <div className='flex-1 space-y-3'>
              {/* Business Name */}
              <div>
                {isEditing ? (
                  <>
                    <input
                      {...register('business_name', { required: 'Business name is required' })}
                      type='text'
                      placeholder='Business Name'
                      className={`text-2xl font-bold w-full px-3 py-1 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.business_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.business_name && (
                      <p className='text-red-500 text-xs mt-1'>{errors.business_name.message}</p>
                    )}
                  </>
                ) : (
                  <h3 className='text-2xl font-bold text-gray-900'>{formValues.business_name}</h3>
                )}
              </div>

              {/* Industry with Icon */}
              <div className='flex items-center gap-2 text-gray-700'>
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
                  />
                </svg>
                {isEditing ? (
                  <input
                    {...register('industry', { required: 'Industry is required' })}
                    type='text'
                    placeholder='Industry'
                    readOnly
                    disabled
                    className={`flex-1 px-2 py-1 border rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.industry ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                ) : (
                  <span className='font-medium'>{formValues.industry}</span>
                )}
              </div>

              {/* Location with Pin Icon */}
              <div className='flex items-center gap-2 text-gray-700'>
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                  />
                </svg>
                {isEditing ? (
                  <div className='flex gap-2 flex-1'>
                    <input
                      {...register('country', { required: 'Country is required' })}
                      type='text'
                      placeholder='Country'
                      className={`flex-1 px-2 py-1 border rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.country ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <input
                      {...register('city', { required: 'City is required' })}
                      type='text'
                      placeholder='City'
                      className={`flex-1 px-2 py-1 border rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.city ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                ) : (
                  <span>
                    {formValues.country}, {formValues.city}
                  </span>
                )}
              </div>
            </div>

            {/* Logo */}
            <div className='flex-shrink-0'>
              {isEditing ? (
                <div className='space-y-2'>
                  {formValues.logo && (
                    <img
                      src={formValues.logo}
                      alt='Logo preview'
                      className='w-32 h-32 object-contain border border-gray-200 p-2 rounded-lg'
                    />
                  )}
                  <input
                    {...register('logo', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL',
                      },
                    })}
                    type='url'
                    placeholder='Logo URL'
                    className={`w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.logo ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
              ) : (
                formValues.logo && (
                  <img
                    src={formValues.logo}
                    alt='Business logo'
                    className='w-32 h-32 bg-black/50 object-contain border border-gray-200 p-2 rounded-lg'
                  />
                )
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>Description</label>
            {isEditing ? (
              <textarea
                {...register('description', { required: 'Description is required' })}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            ) : (
              <p className='text-gray-900'>{formValues.description}</p>
            )}
          </div>

          {/* Target Audience and Voice & Tone */}
          <div className='grid grid-cols-2 gap-6'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Target Audience
              </label>
              {isEditing ? (
                <textarea
                  {...register('audience', { required: 'Target audience is required' })}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.audience ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              ) : (
                <p className='text-gray-900'>{formValues.audience}</p>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Voice & Tone</label>
              {isEditing ? (
                <textarea
                  {...register('voice_tone', { required: 'Voice and tone is required' })}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.voice_tone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              ) : (
                <p className='text-gray-900'>{formValues.voice_tone}</p>
              )}
            </div>
          </div>

          {/* Pills Section */}
          <div className='grid grid-cols-1 gap-6'>
            {/* Selling Points */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Selling Points</label>
              <div className='flex flex-wrap gap-2'>
                {sellingPointsFields.map((field, index) => (
                  <div
                    key={field.id}
                    className='inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm'
                  >
                    {isEditing ? (
                      <>
                        <input
                          {...register(`selling_points.${index}`)}
                          type='text'
                          className='bg-transparent border-none outline-none w-full min-w-[60px] max-w-[200px]'
                          placeholder='Enter point'
                        />
                        <button
                          type='button'
                          onClick={() => removeSellingPoint(index)}
                          className='hover:text-blue-900'
                        >
                          <X className='w-3 h-3' />
                        </button>
                      </>
                    ) : (
                      <span className='inline-block px-3 py-4'>
                        {formValues.selling_points[index]}
                      </span>
                    )}
                  </div>
                ))}
                {isEditing && (
                  <button
                    type='button'
                    onClick={() => appendSellingPoint('')}
                    className='inline-flex items-center gap-1 bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm hover:bg-blue-100 border border-blue-200 border-dashed'
                  >
                    <Plus className='w-3 h-3' />
                    Add
                  </button>
                )}
              </div>
            </div>

            {/* Call to Actions */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Call to Actions
              </label>
              <div className='flex flex-wrap gap-2'>
                {ctaFields.map((field, index) => (
                  <div
                    key={field.id}
                    className='inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm'
                  >
                    {isEditing ? (
                      <>
                        <input
                          {...register(`call_to_actions.${index}`)}
                          type='text'
                          className='bg-transparent border-none outline-none w-full min-w-[60px] max-w-[200px]'
                          placeholder='Enter CTA'
                        />
                        <button
                          type='button'
                          onClick={() => removeCta(index)}
                          className='hover:text-green-900'
                        >
                          <X className='w-3 h-3' />
                        </button>
                      </>
                    ) : (
                      <span className='inline-block px-3 py-4'>
                        {formValues.call_to_actions[index]}
                      </span>
                    )}
                  </div>
                ))}
                {isEditing && (
                  <button
                    type='button'
                    onClick={() => appendCta('')}
                    className='inline-flex items-center gap-1 bg-green-50 text-green-600 px-3 py-1 rounded-full text-sm hover:bg-green-100 border border-green-200 border-dashed'
                  >
                    <Plus className='w-3 h-3' />
                    Add
                  </button>
                )}
              </div>
            </div>

            {/* Core Values */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Core Values</label>
              <div className='flex flex-wrap gap-2'>
                {coreValuesFields.map((field, index) => (
                  <div
                    key={field.id}
                    className='inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm'
                  >
                    {isEditing ? (
                      <>
                        <input
                          {...register(`core_values.${index}`)}
                          type='text'
                          className='bg-transparent border-none outline-none w-full min-w-[60px] max-w-[200px]'
                          placeholder='Enter value'
                        />
                        <button
                          type='button'
                          onClick={() => removeCoreValue(index)}
                          className='hover:text-purple-900'
                        >
                          <X className='w-3 h-3' />
                        </button>
                      </>
                    ) : (
                      <span className='inline-block px-3 py-4'>
                        {formValues.core_values[index]}
                      </span>
                    )}
                  </div>
                ))}
                {isEditing && (
                  <button
                    type='button'
                    onClick={() => appendCoreValue('')}
                    className='inline-flex items-center gap-1 bg-purple-50 text-purple-600 px-3 py-1 rounded-full text-sm hover:bg-purple-100 border border-purple-200 border-dashed'
                  >
                    <Plus className='w-3 h-3' />
                    Add
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
};

export default SummaryForm;
